import { useState } from 'react';
import { WALLET_LIMIT } from '@shared/constants';
import { UseLoadWalletReturn } from '@modules/settings/my-numbers/my-numbers.types';
import { useTranslation } from 'react-i18next';
import { PaymentGatewayEnum, PaymentIntentPurposeEnum, useLoadWalletMutation } from '@/types/generated/graphql';
import { UsePaymentMethodsListReturn } from '@modules/settings/subscription/hooks/use-payment-methods-list';
import { useCreatePaymentIntent } from '@modules/settings/wallet/hooks/use-create-payment-intent';
import { parseChargebeeErrorMessage } from '@shared/utils/parseChargebeeErrorMessage';
import { notification } from 'antd';
import { cache } from '@/container/apollo-client/cache';

export const useLoadWallet = (): UseLoadWalletReturn => {
  const [limitError, setLimitError] = useState<string | null>(null);
  const [amount, setLoadAmount] = useState<number>(WALLET_LIMIT.LOAD_WALLET);
  const { t } = useTranslation();
  const { createPaymentIntent } = useCreatePaymentIntent();

  const [executeLoadWallet, { loading: loadWalletLoading }] = useLoadWalletMutation({
    onCompleted: async () => {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'fetchJournalEntries' });
      cache.gc();
    },
    onError: (error) => {
      notification.error({ message: error?.message });
    },
  });

  const setAmount = (value?: number) => {
    if (typeof value != 'number') return;

    setLimitError('');
    setLoadAmount(value);

    if (value < WALLET_LIMIT.LOAD_WALLET) {
      const error = t('minimum_load_amount_is_{amount}', { amount: WALLET_LIMIT.LOAD_WALLET });
      setLimitError(error);
    }
  };

  const loadWallet = async (
    walletAmount: number,
    paymentMethodsList: UsePaymentMethodsListReturn,
    onSuccess: () => void
  ) => {
    if (walletAmount < WALLET_LIMIT.LOAD_WALLET) {
      setLimitError(t('minimum_load_amount_is_{amount}', { amount: WALLET_LIMIT.LOAD_WALLET }));
      return;
    }

    const isPayPal = paymentMethodsList?.paymentMethod?.paypal?.email !== null;

    paymentMethodsList?.setProcessing(true);
    setLimitError('');

    try {
      let gatewayAccountId = paymentMethodsList?.paymentMethod?.gatewayAccountId;
      let gatewayToken: string | undefined;
      let intentAmount = walletAmount * 100;

      if (!isPayPal) {
        const intentResponse = await createPaymentIntent({
          paymentIntentPurpose: PaymentIntentPurposeEnum?.load_wallet,
          paymentGateway: PaymentGatewayEnum?.stripe,
          loadWallet: {
            amount: intentAmount,
            referenceId: paymentMethodsList?.paymentMethod?.referenceId || '',
          },
          gatewayAccountId: paymentMethodsList?.paymentMethod?.gatewayAccountId,
        });

        if (intentResponse?.errors && intentResponse.errors.length > 0) {
          throw new Error(intentResponse?.data?.createPaymentIntent?.message ?? 'Error');
        }

        const intent = intentResponse?.data?.createPaymentIntent?.data;
        const cbInstance = window?.Chargebee?.getInstance?.();
        //@ts-expect-error Chargebee types not available
        const threeDSHandler = await cbInstance.load3DSHandler();
        threeDSHandler.setPaymentIntent(intent);
        const chargedIntent = await threeDSHandler.handleCardPayment();

        if (chargedIntent?.active_payment_attempt?.error_text) {
          const { message } = parseChargebeeErrorMessage(chargedIntent.active_payment_attempt.error_text);
          setLimitError(message);
          paymentMethodsList?.setProcessing(false);
          return;
        }

        intentAmount = chargedIntent?.amount ?? intentAmount;
        gatewayAccountId = chargedIntent?.gateway_account_id;
        gatewayToken = chargedIntent?.active_payment_attempt?.id_at_gateway;
      }

      const paymentResult = await executeLoadWallet({
        variables: {
          input: {
            amount: intentAmount,
            gatewayAccountId: (isPayPal ? paymentMethodsList?.paymentMethod?.gatewayAccountId : gatewayAccountId) ?? '',
            ...(isPayPal
              ? { referenceId: paymentMethodsList?.paymentMethod?.referenceId }
              : { gatewayToken: gatewayToken }),
            paymentGateway: isPayPal ? PaymentGatewayEnum?.paypal : PaymentGatewayEnum?.stripe,
            description: 'load wallet',
          },
        },
      });

      onSuccess();
      return paymentResult;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('an_unexpected_error_occurred');
      setLimitError(errorMessage);
    } finally {
      paymentMethodsList?.setProcessing(false);
    }
  };

  return {
    amount,
    setAmount,
    limitError,
    loadWalletLoading,
    loadWallet,
  };
};
